/**
 * SourceReplacedNotice — the outcome of "upload updated version".
 *
 * Done: "<name> was updated", with "Check impacted sections"; it hides itself
 * after a few seconds. Failed: why, and the old document is still there; it
 * stays until dismissed. While the replace runs, the document's own row shows
 * a spinner instead (``DocRow`` ``replacing``).
 *
 * SRP: pure presentation. The shell owns the replace call and wires
 * ``onCheckImpact``.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useRef } from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";

export type SourceReplacedState =
  | { status: "done"; name: string; oldDocId: string; newDocId: string }
  | { status: "failed"; name: string; message: string };

const useStyles = makeStyles({
  notice: {
    position: "fixed",
    left: "12px",
    right: "12px",
    bottom: "12px",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    padding: "10px 12px",
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderLeft: `3px solid ${brandColors.primary}`,
    borderRadius: "4px",
    boxShadow: "0 8px 24px rgba(15, 10, 30, 0.18)",
    fontFamily: fonts.body,
    fontSize: "12px",
    color: inkColors.ink,
  },
  failed: {
    borderLeft: `3px solid ${statusColors.danger}`,
  },
  text: {
    flex: 1,
    minWidth: 0,
    lineHeight: 1.4,
  },
  name: {
    fontWeight: 700,
    wordBreak: "break-word",
  },
  action: {
    flexShrink: 0,
    fontSize: "11px",
    fontWeight: 700,
    padding: "6px 10px",
    borderRadius: "3px",
    cursor: "pointer",
    border: "none",
    background: brandColors.primary,
    color: "var(--on-brand)",
    fontFamily: fonts.body,
  },
  close: {
    flexShrink: 0,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: inkColors.ink4,
    fontSize: "14px",
    padding: "0 2px",
  },
});

export interface SourceReplacedNoticeProps {
  state: SourceReplacedState;
  /** Open the impacted-sections view for the updated document. */
  onCheckImpact?: (name: string) => void;
  onDismiss: () => void;
}

/** How long a "was updated" notice stays before hiding itself. */
export const DONE_NOTICE_MS = 10_000;

export const SourceReplacedNotice: React.FC<SourceReplacedNoticeProps> = ({ state, onCheckImpact, onDismiss }) => {
  const styles = useStyles();
  // The shell passes a fresh ``onDismiss`` every render; keyed on it, the
  // timer would restart on each one and never fire.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  useEffect(() => {
    if (state.status !== "done") return;
    const timer = window.setTimeout(() => dismissRef.current(), DONE_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [state]);
  return (
    <output
      className={mergeClasses(styles.notice, state.status === "failed" && styles.failed)}
      data-testid="source-replaced-notice"
    >
      <div className={styles.text}>
        {state.status === "done" && (
          <>
            <span className={styles.name}>{state.name}</span> was updated
          </>
        )}
        {state.status === "failed" && (
          <>
            <span className={styles.name}>{state.name}</span> was not updated: {state.message} The old version is still
            in the library.
          </>
        )}
      </div>
      {state.status === "done" && onCheckImpact && (
        <button
          type="button"
          className={styles.action}
          onClick={() => onCheckImpact(state.name)}
          data-testid="source-replaced-check-impact"
        >
          Check impacted sections
        </button>
      )}
      <button type="button" className={styles.close} onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
    </output>
  );
};

export default SourceReplacedNotice;
